import { computed, ref, toValue, watch, onScopeDispose, type MaybeRefOrGetter } from "vue";
import {
  clientToolsVersion,
  getClientTool,
} from "#bb/lib/chatClientTools";
import { getChatTool } from "#bb/lib/chatToolManifest";
import { eventBus } from "#bb/lib/eventBus";
import type {
  ChatMessage,
  ChatPart,
  ChatSurface,
  CompletionPart,
  ConversationDetail,
  ConversationSummary,
  ResourceArtifact,
  StartChatRequest,
  StartChatResult,
  ToolUiPart,
  UiMessageDto,
} from "../types/chat";
import {
  applyTurnDelta,
  createMessage,
  drainSseFrames,
  finalizeParts,
} from "../lib/stream-reducer";
import {
  errorMessage,
  pendingToolToMessage,
  transcriptToMessage,
} from "../lib/transcript";

/**
 * Provider-agnostic chat client for a single conversation surface.
 *
 * Drives the server-side agentic chat contract:
 *  - `POST /v1/chat/{surface}/conversations` to start a conversation,
 *  - `POST /v1/chat/{surface}/conversations/{id}/messages` (SSE) to stream a reply,
 *  - `GET`/`DELETE` on the same paths for history.
 *
 * The stream is consumed with the native `fetch` ReadableStream API (NOT the
 * host `useApi`/`useFetch`, which can't surface a progressive body), while still
 * honouring the host auth contract: `Authorization: Bearer <id_token>` and the
 * chat-surface site header `x-site-id` (note: data APIs use `x-site`, chat uses
 * `x-site-id`).
 *
 * State is per-call (not a global singleton), so multiple chat surfaces can
 * mount independently without leaking messages or aborting each other.
 */
export interface UseChatOptions {
  /** Resource path the chat is scoped to. Required for candidate (agent-gated) chat. */
  contextPath?: MaybeRefOrGetter<string | undefined>;
  /** Optional human-friendly conversation title. */
  title?: MaybeRefOrGetter<string | undefined>;
  /** Admin-only: override the agent file path (null/undefined = auto-resolve from context). */
  agentPath?: MaybeRefOrGetter<string | null | undefined>;
  /**
   * The user the bound resource is about (e.g. the candidate whose report is being viewed via the
   * `?user=` query). Pinned server-side at chat start so report tools read the right subject's data.
   */
  subjectUserId?: MaybeRefOrGetter<string | null | undefined>;
  /** Active report-provider block id for an admin report context. */
  reportProviderId?: MaybeRefOrGetter<string | null | undefined>;
  /**
   * Invoked when the server emits a terminal `complete` part (an assessment finished). Receives the
   * completion payload including the optional report `navigation`. A host can follow the navigation
   * (built-in candidate report) or intercept it here to render its own report.
   */
  onComplete?: (completion: CompletionPart) => void;
}

export function useChat(
  surface: ChatSurface,
  siteId: MaybeRefOrGetter<string | undefined>,
  options: UseChatOptions = {},
) {
  const config = useRuntimeConfig();
  const { authHeaders } = useChatHttp();

  const messages = ref<ChatMessage[]>([]);
  const isStreaming = ref(false);
  const error = ref<string | null>(null);
  const conversation = ref<StartChatResult | null>(null);
  /**
   * The latest live resource-draft snapshot (B7). Transient UI state, not transcript: replaced
   * (never appended) each time the editor agent mutates the draft, and cleared on reset.
   */
  const resourceArtifact = ref<ResourceArtifact | null>(null);
  /**
   * The terminal completion payload (assessment finished), or null until the server emits a `complete`
   * part. Carries the optional report `navigation`. Set once per conversation; cleared on reset/reload.
   */
  const completion = ref<CompletionPart | null>(null);

  const agentName = computed(() => conversation.value?.agentName ?? null);
  const model = computed(() => conversation.value?.model ?? null);
  const conversationId = computed(() => conversation.value?.conversationId ?? null);

  /**
   * The pending tool call currently awaiting the user, if any. Answerable
   * interactive blocks park in `input-available`; confirm-before-write editor
   * tools (e.g. `resource_persist`) park in `approval-requested`. Either way the
   * backend rejects a free-text message while a tool is pending, so the UI uses
   * this to gate the composer until the block is answered or the card is
   * approved/rejected.
   */
  const pendingTool = computed<ToolUiPart | null>(() => {
    for (const message of messages.value) {
      for (const part of message.parts) {
        if (
          part.type === "tool" &&
          part.tool &&
          (part.tool.state === "input-available" || part.tool.state === "approval-requested")
        ) {
          return part.tool;
        }
      }
    }
    return null;
  });
  const hasPendingTool = computed(() => pendingTool.value !== null);

  let abortController: AbortController | null = null;
  // Monotonic guard so a superseded/aborted stream never mutates shared state
  // after a newer send has started.
  let streamSeq = 0;

  const baseUrl = () => `${config.public.apiUrl}/v1/chat/${surface}/conversations`;

  const headers = (extra?: Record<string, string>) => authHeaders(toValue(siteId), extra);

  /** Starts a new conversation, resolving the governing agent for the context. */
  async function start(expectedStreamSeq: number): Promise<StartChatResult> {
    const body: StartChatRequest = {
      contextPath: toValue(options.contextPath) ?? null,
      title: toValue(options.title) ?? null,
      agentPath: toValue(options.agentPath) ?? null,
      subjectUserId: toValue(options.subjectUserId) ?? null,
      reportProviderId: toValue(options.reportProviderId) ?? null,
    };
    const result = await $fetch<StartChatResult>(baseUrl(), {
      method: "POST",
      headers: headers({ "Content-Type": "application/json" }),
      body,
    });
    if (expectedStreamSeq === streamSeq) {
      conversation.value = result;
    }
    return result;
  }

  /**
   * Sends a user message and streams the assistant reply. Starts a conversation
   * on first use. Aborts any in-flight stream first.
   */
  async function send(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }
    if (!import.meta.client) {
      // Streaming is a client-only concern; never initiate it during SSR.
      return;
    }

    // Supersede any in-flight stream before starting a new one.
    cancel();
    const mySeq = ++streamSeq;
    error.value = null;

    messages.value = [
      ...messages.value,
      createMessage(`user-${crypto.randomUUID()}`, "user", [
        {
          key: "message:user",
          type: "message",
          text: trimmed,
          isStreaming: false,
          sequence: 0,
        },
      ]),
    ];

    await streamUserTurn(trimmed, mySeq);
  }

  /**
   * Silently begins the conversation by streaming the agent's opening turn from a seed message
   * WITHOUT rendering the seed as a user turn. Used by auto-start (guided assessment) so the agent
   * greets and asks the first question with no typing and no visible "Start" bubble. No-op if a
   * conversation is already started, the transcript is non-empty, or a stream is already in flight —
   * so it can be called idempotently when the chat opens.
   */
  async function begin(seed = "Start"): Promise<void> {
    const trimmed = seed.trim();
    if (!trimmed) {
      return;
    }
    if (!import.meta.client) {
      return;
    }
    // Only auto-begin a fresh conversation; never interrupt an existing one or a live stream.
    if (conversation.value || messages.value.length > 0 || isStreaming.value) {
      return;
    }

    cancel();
    const mySeq = ++streamSeq;
    error.value = null;

    await streamUserTurn(trimmed, mySeq);
  }

  /**
   * Shared tail for {@link send} and {@link begin}: ensures a conversation exists, then streams the
   * assistant reply for `message`. The caller owns the optimistic transcript mutation (a user bubble
   * for `send`, nothing for `begin`) and the `streamSeq` bump done before this call.
   */
  async function streamUserTurn(message: string, mySeq: number): Promise<void> {
    try {
      const convo = conversation.value ?? (await start(mySeq));
      if (mySeq !== streamSeq) {
        return; // superseded while starting
      }
      await runStream(
        `${baseUrl()}/${convo.conversationId}/messages`,
        {
          message,
          contextPath: toValue(options.contextPath) ?? null,
          reportProviderId: toValue(options.reportProviderId) ?? null,
        },
        mySeq,
      );
    } catch (err: unknown) {
      if (mySeq === streamSeq) {
        error.value = errorMessage(err);
      }
    } finally {
      if (mySeq === streamSeq) {
        isStreaming.value = false;
      }
    }
  }

  /**
   * Submits the user's answer to a pending interactive tool call (an inline
   * building block) and streams the agent's continuation into a *new* assistant
   * turn. The answered block is resolved optimistically (the backend does not
   * echo an `output-available` part); if the round-trip fails the optimistic
   * resolution is rolled back so the block stays answerable.
   */
  async function submitToolResult(
    invocationId: string,
    toolName: string,
    result: unknown,
  ): Promise<void> {
    if (!import.meta.client) {
      return;
    }
    const convo = conversation.value;
    if (!convo) {
      return;
    }

    // Guard against duplicate / stale submissions: the local part must still
    // exist, match the tool, and be awaiting an answer.
    const located = locateToolPart(invocationId);
    if (
      !located ||
      located.tool.name !== toolName ||
      located.tool.state !== "input-available"
    ) {
      return;
    }

    const previousState = located.tool.state;
    const previousOutput = located.tool.output;

    cancel();
    const mySeq = ++streamSeq;
    error.value = null;

    // Optimistically resolve the block so it goes read-only immediately.
    mutateToolPart(invocationId, (tool) => ({
      ...tool,
      state: "output-available",
      output: result,
    }));

    try {
      await runStream(
        `${baseUrl()}/${convo.conversationId}/tool-results`,
        { invocationId, toolName, result },
        mySeq,
      );
    } catch (err: unknown) {
      if (mySeq === streamSeq) {
        if (await resyncOnSettlementConflict(err, mySeq)) {
          return;
        }
        error.value = errorMessage(err);
        // Roll back the optimistic resolution: the server did not accept the
        // answer, so the block must stay interactive for a retry.
        mutateToolPart(invocationId, (tool) => ({
          ...tool,
          state: previousState,
          output: previousOutput,
        }));
      }
    } finally {
      if (mySeq === streamSeq) {
        isStreaming.value = false;
      }
    }
  }

  /**
   * Cancels (declines/skips) the pending interactive tool call the user has chosen
   * not to answer. The block is resolved optimistically to `output-denied` and the
   * agent's acknowledgement streams into a *new* assistant turn; on failure the
   * optimistic resolution is rolled back so the block stays answerable. Mirrors
   * {@link submitToolResult} but hits the `/cancel` endpoint with no answer payload.
   */
  async function cancelPendingTool(
    invocationId: string,
    toolName: string,
  ): Promise<void> {
    if (!import.meta.client) {
      return;
    }
    const convo = conversation.value;
    if (!convo) {
      return;
    }

    // Guard against duplicate / stale cancellations: the local part must still
    // exist, match the tool, and be awaiting an answer.
    const located = locateToolPart(invocationId);
    if (
      !located ||
      located.tool.name !== toolName ||
      located.tool.state !== "input-available"
    ) {
      return;
    }

    const previousState = located.tool.state;
    const previousOutput = located.tool.output;

    cancel();
    const mySeq = ++streamSeq;
    error.value = null;

    // Optimistically mark the block declined so it goes read-only immediately.
    mutateToolPart(invocationId, (tool) => ({
      ...tool,
      state: "output-denied",
      output: undefined,
    }));

    try {
      await runStream(
        `${baseUrl()}/${convo.conversationId}/cancel`,
        { invocationId, toolName },
        mySeq,
      );
    } catch (err: unknown) {
      if (mySeq === streamSeq) {
        if (await resyncOnSettlementConflict(err, mySeq)) {
          return;
        }
        error.value = errorMessage(err);
        // Roll back: the server did not accept the cancellation, so the block
        // must stay interactive for a retry.
        mutateToolPart(invocationId, (tool) => ({
          ...tool,
          state: previousState,
          output: previousOutput,
        }));
      }
    } finally {
      if (mySeq === streamSeq) {
        isStreaming.value = false;
      }
    }
  }

  /**
   * Opens an SSE stream against `url`, reading assistant parts into a fresh
   * assistant turn. Shared by free-text messages and tool-result round-trips.
   */
  async function runStream(
    url: string,
    body: Record<string, unknown>,
    mySeq: number,
  ): Promise<void> {
    const controller = new AbortController();
    abortController = controller;
    isStreaming.value = true;

    const turnId = `assistant-${crypto.randomUUID()}`;
    let turnAdded = false;

    const upsertTurn = (mutate: (turn: ChatMessage) => ChatMessage) => {
      const next = messages.value.slice();
      const index = next.findIndex((m) => m.id === turnId);
      if (index === -1) {
        next.push(mutate(createMessage(turnId, "assistant")));
      } else {
        next[index] = mutate(next[index]!);
      }
      messages.value = next;
      turnAdded = true;
    };

    const response = await fetch(url, {
      method: "POST",
      headers: headers({
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      }),
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      throw Object.assign(new Error(`Chat stream failed with status ${response.status}.`), {
        status: response.status,
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    const handle = (dto: UiMessageDto) => {
      if (mySeq !== streamSeq) {
        return;
      }
      // `resource` parts are transient live-preview state, NOT transcript — keep the latest
      // snapshot and skip the reducer so they never appear as message parts in the thread.
      if (dto.type === "resource") {
        if (dto.resource) {
          resourceArtifact.value = dto.resource;

          // Bridge the draft to the editor via event bus so the resource editor can
          // apply AI edits live (WS-6). Harmless when no listener exists.
          if (dto.resource.content && dto.resource.path) {
            try {
              const parsed = JSON.parse(dto.resource.content);
              if (Array.isArray(parsed.content)) {
                eventBus.emit("resource:draft-updated", {
                  path: dto.resource.path,
                  content: parsed.content,
                  blockId: dto.resource.lastEditedBlockId ?? undefined,
                });
              }
            } catch {
              // Malformed draft content — skip live update.
            }
          }
        }
        return;
      }
      // `complete` is a terminal, transient signal (an assessment finished), NOT transcript — capture
      // the payload and fire the host callback; never merge it as a message part. The host decides
      // whether to follow `navigation` (built-in report) or intercept it (own report).
      if (dto.type === "complete") {
        if (dto.completion) {
          completion.value = dto.completion;
          options.onComplete?.(dto.completion);
        }
        return;
      }
      upsertTurn((turn) => ({ ...turn, parts: applyTurnDelta(turn.parts, dto) }));
    };

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = drainSseFrames(buffer);
        buffer = rest;
        for (const event of events) {
          handle(event);
        }
      }
      // Flush any trailing bytes / final frame without a trailing separator.
      buffer += decoder.decode();
      const { events } = drainSseFrames(buffer + "\n\n");
      for (const event of events) {
        handle(event);
      }
    } finally {
      if (mySeq === streamSeq && turnAdded) {
        upsertTurn((turn) => ({ ...turn, parts: finalizeParts(turn.parts) }));
      }
      if (abortController === controller) {
        abortController = null;
      }
    }
  }

  /** Finds the merged tool part for an invocation, if present in the transcript. */
  function locateToolPart(invocationId: string): { part: ChatPart; tool: ToolUiPart } | null {
    for (const message of messages.value) {
      for (const part of message.parts) {
        if (part.type === "tool" && part.tool?.invocationId === invocationId) {
          return { part, tool: part.tool };
        }
      }
    }
    return null;
  }

  /** Immutably replaces the tool payload of the part matching `invocationId`. */
  function mutateToolPart(invocationId: string, mutate: (tool: ToolUiPart) => ToolUiPart): void {
    messages.value = messages.value.map((message) => {
      let changed = false;
      const parts = message.parts.map((part) => {
        if (part.type === "tool" && part.tool?.invocationId === invocationId) {
          changed = true;
          return { ...part, tool: mutate(part.tool) };
        }
        return part;
      });
      return changed ? { ...message, parts } : message;
    });
  }

  /**
   * Handles a settlement failure: a 409 means the server already claimed this tool's settlement (e.g.
   * a racing tab, or a reload mid-settle), so the local optimistic state may be stale — resync from the
   * server rather than rolling back to a phantom-pending block. Returns true when the conflict was
   * handled (caller should skip its local rollback). Any other error is left for the caller.
   */
  async function resyncOnSettlementConflict(err: unknown, mySeq: number): Promise<boolean> {
    const status = (err as { status?: number } | null)?.status;
    if (status !== 409 || mySeq !== streamSeq) {
      return false;
    }
    const convo = conversation.value;
    if (!convo) {
      return false;
    }
    try {
      await loadConversation(convo.conversationId);
    } catch {
      // If the resync itself fails, fall through to the caller's rollback handling.
      return false;
    }
    return true;
  }

  /** Aborts the in-flight stream, if any. Does not surface an error. */
  function cancel(): void {
    abortController?.abort();
    abortController = null;
    isStreaming.value = false;
  }

  /** Clears the transcript and conversation, ready to start fresh. */
  function reset(): void {
    cancel();
    streamSeq += 1; // invalidate any late callbacks from a prior stream
    messages.value = [];
    conversation.value = null;
    resourceArtifact.value = null;
    completion.value = null;
    error.value = null;
  }

  /** Lists recent conversations for this surface/site. */
  async function listConversations(includeArchived = false): Promise<ConversationSummary[]> {
    return await $fetch<ConversationSummary[]>(baseUrl(), {
      method: "GET",
      headers: headers(),
      query: { includeArchived },
    });
  }

  /** Loads a conversation transcript and replaces the local state with it. */
  async function loadConversation(id: string): Promise<ConversationDetail> {
    const detail = await $fetch<ConversationDetail>(`${baseUrl()}/${id}`, {
      method: "GET",
      headers: headers(),
    });
    conversation.value = {
      conversationId: detail.id,
      agentName: detail.agentName ?? "",
      model: detail.model,
      agentPath: detail.agentPath,
      defaultOpen: false,
    };
    const restored = detail.messages.map(transcriptToMessage);
    // Reconstruct the actionable pending tool block/card (if any) from the server's
    // pending-tool marker, so a reloaded conversation re-renders the interactive prompt and
    // keeps the composer gated. The transcript only carries *answered* tool parts, so without
    // this a refresh mid-prompt would strand the user (composer blocked, nothing to answer).
    const pendingMessage = pendingToolToMessage(detail.pendingToolPartsJson, restored);
    if (pendingMessage) {
      restored.push(pendingMessage);
    }
    messages.value = restored;
    // Restore the in-progress draft preview (B7) if the conversation has an open edit session, so a
    // reload re-renders the split-view and any confirm card has its draft; else start clean.
    resourceArtifact.value = detail.resource ?? null;
    completion.value = null;
    error.value = null;
    return detail;  }

  /** Soft-archives a conversation. */
  async function deleteConversation(id: string): Promise<void> {
    await $fetch(`${baseUrl()}/${id}`, {
      method: "DELETE",
      headers: headers(),
    });
  }

  onScopeDispose(() => {
    streamSeq += 1;
    cancel();
  });

  /**
   * Approves or rejects a pending confirm-before-write tool call. Two tools park this
   * way today: `resource_persist` (save the in-progress report/assessment — settled at
   * `/resource-edit`) and `run_automation` (start a stored automation — settled at
   * `/automation-run`). The endpoint is resolved from the tool name; for any other tool
   * the resource-edit endpoint is used as a safe default. On approval the server performs
   * the trusted action (the draft / automation arguments it holds for the conversation are
   * never sent from the browser) and resumes the run; on rejection the action is declined.
   * The card is resolved optimistically (`output-available` on approve, `output-denied` on
   * reject) and the agent's acknowledgement streams into a *new* assistant turn; on failure
   * the optimistic resolution is rolled back so the card stays actionable for a retry.
   * Mirrors {@link cancelPendingTool}.
   */
  function confirmEndpointFor(toolName: string): string {
    return toolName === "run_automation" ? "automation-run" : "resource-edit";
  }

  async function confirmTool(
    invocationId: string,
    toolName: string,
    approve: boolean,
    summary?: string,
  ): Promise<void> {
    if (!import.meta.client) {
      return;
    }
    const convo = conversation.value;
    if (!convo) {
      return;
    }

    // Guard against duplicate / stale confirmations: the local part must still
    // exist, match the tool, and be awaiting approval.
    const located = locateToolPart(invocationId);
    if (
      !located ||
      located.tool.name !== toolName ||
      located.tool.state !== "approval-requested"
    ) {
      return;
    }

    // Live-preview mutation only applies to the resource editor; the automation
    // run confirmation has no draft artifact bound to it.
    const affectsResource = toolName !== "run_automation";

    const previousState = located.tool.state;
    const previousOutput = located.tool.output;
    const previousArtifact = resourceArtifact.value;

    cancel();
    const mySeq = ++streamSeq;
    error.value = null;

    // Optimistically resolve the card so it goes read-only immediately.
    mutateToolPart(invocationId, (tool) => ({
      ...tool,
      state: approve ? "output-available" : "output-denied",
      output: undefined,
    }));

    // Reflect the settlement in the live preview: approve flips it to "Saved",
    // reject discards the draft (the server clears it either way).
    if (affectsResource) {
      if (approve) {
        if (resourceArtifact.value) {
          resourceArtifact.value = { ...resourceArtifact.value, saved: true };
        }
      } else {
        resourceArtifact.value = null;
      }
    }

    try {
      await runStream(
        `${baseUrl()}/${convo.conversationId}/${confirmEndpointFor(toolName)}`,
        { invocationId, toolName, approve, summary },
        mySeq,
      );
    } catch (err: unknown) {
      if (mySeq === streamSeq) {
        if (await resyncOnSettlementConflict(err, mySeq)) {
          return;
        }
        error.value = errorMessage(err);
        // Roll back: the server did not accept the confirmation, so the card must
        // stay actionable for a retry.
        mutateToolPart(invocationId, (tool) => ({
          ...tool,
          state: previousState,
          output: previousOutput,
        }));
        if (affectsResource) {
          resourceArtifact.value = previousArtifact;
        }
      }
    } finally {
      if (mySeq === streamSeq) {
        isStreaming.value = false;
      }
    }
  }

  /**
   * Browser-mediated tool auto-fulfilment (E3 slice 2).
   *
   * Some parked tools are fulfilled in the browser, not by a human: when a tool
   * parks in `input-available` AND a client handler is registered for its name
   * (e.g. the open automation editor registered `automation_add_node`), run the
   * handler and feed its result back via {@link submitToolResult} to resume the
   * agent. Interactive blocks (radio groups, etc.) are never registered here, so
   * they continue to await the user. Confirm-before-write tools park in
   * `approval-requested` and are intentionally skipped (they need explicit user
   * approval).
   *
   * The watch keys on both `pendingTool` and the registry `version` so a tool
   * that parked BEFORE its handler was registered — editor opened late, or a
   * conversation reloaded mid-flow — is still picked up once the handler appears.
   * `autoHandled` keys on invocation id so a single parked tool is run exactly
   * once (a non-409 submit failure surfaces an error rather than re-running the
   * side-effecting handler).
   */
  const autoHandled = new Set<string>();
  /** Tracks pending timeouts for editor tools awaiting a handler. */
  const editorToolTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  if (import.meta.client) {
    watch(
      [pendingTool, clientToolsVersion],
      async ([tool]) => {
        if (!tool || tool.state !== "input-available") {
          return;
        }
        if (autoHandled.has(tool.invocationId)) {
          return;
        }
        const handler = getClientTool(tool.name);
        if (handler) {
          // Clear any pending timeout — handler arrived in time.
          const existing = editorToolTimeouts.get(tool.invocationId);
          if (existing) {
            clearTimeout(existing);
            editorToolTimeouts.delete(tool.invocationId);
          }

          autoHandled.add(tool.invocationId);

          const convo = conversation.value;
          let result: unknown;
          try {
            result = await handler(tool.input, {
              conversationId: convo?.conversationId ?? "",
              invocationId: tool.invocationId,
            });
          } catch (err: unknown) {
            result = { ok: false, error: errorMessage(err) };
          }
          await submitToolResult(tool.invocationId, tool.name, result);
          return;
        }

        // No handler registered. For editor tools (automation_*, resource_move_block,
        // etc.) that require a mounted editor, start a timeout: if the handler doesn't
        // appear within 3 seconds, return an error so the AI can recover.
        const descriptor = getChatTool(tool.name);
        if (descriptor?.kind === "editor" && !editorToolTimeouts.has(tool.invocationId)) {
          const invocationId = tool.invocationId;
          const toolName = tool.name;
          editorToolTimeouts.set(
            invocationId,
            setTimeout(async () => {
              editorToolTimeouts.delete(invocationId);
              // Re-check: handler may have appeared during the timeout.
              if (autoHandled.has(invocationId) || getClientTool(toolName)) {
                return;
              }
              autoHandled.add(invocationId);
              await submitToolResult(invocationId, toolName, {
                ok: false,
                error: `The editor for '${toolName}' is not open. Use fs_navigate to open it first, then retry.`,
              });
            }, 3000),
          );
        }
      },
      { immediate: true },
    );

    onScopeDispose(() => {
      for (const timeout of editorToolTimeouts.values()) {
        clearTimeout(timeout);
      }
      editorToolTimeouts.clear();
    });
  }

  return {
    messages,
    isStreaming,
    error,
    conversation,
    conversationId,
    agentName,
    model,
    pendingTool,
    hasPendingTool,
    resourceArtifact,
    completion,
    start,
    send,
    begin,
    submitToolResult,
    cancelPendingTool,
    confirmTool,
    cancel,
    reset,
    listConversations,
    loadConversation,
    deleteConversation,
  };
}
